import React, { useEffect, useMemo, useRef, useState } from 'react';

import iconChevronDown from '../../images/icon-chevron-down.svg';
import { getSteppedQuantities } from '../../utils/discounts';

const PAGE_SIZE = 200;

export default function QuantityDropdown({
  value,
  onChange,
  label = null,
  minQty = 1,
  maxQty = Infinity,
  step = 1,
  pageSize = PAGE_SIZE,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const selectedRef = useRef(null);

  const { options, hasMore, hasBack } = useMemo(
    () => getSteppedQuantities(minQty, maxQty, step, page, pageSize),
    [minQty, maxQty, step, page, pageSize]
  );
  const selectedValue = parseInt(value, 10) || options[0] || minQty;

  useEffect(() => {
    setPage(1);
    setIsOpen(false);
  }, [minQty, maxQty, step]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest' });
    } else {
      menuRef.current?.scrollTo({ top: 0 });
    }
  }, [isOpen, page, minQty, step]);

  return (
    <div className={`product-quantity-dropdown ${isOpen ? 'is-open' : ''}`} ref={rootRef}>
      {label ? <span className="product-quantity-select-label">{label}</span> : null}
      <div className="product-quantity-dropdown-control">
        <button
          type="button"
          className="product-quantity-dropdown-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
        >
          <span>{selectedValue}</span>
          <img className="product-quantity-dropdown-chevron" src={iconChevronDown} alt="" aria-hidden="true" />
        </button>
        {isOpen ? (
          <ul ref={menuRef} className="product-quantity-dropdown-menu" role="listbox">
            {hasBack ? (
              <li>
                <button
                  type="button"
                  className="product-quantity-dropdown-more"
                  onClick={() => setPage((current) => current - 1)}
                >
                  Back
                </button>
              </li>
            ) : null}
            {options.map((qty) => {
              const isSelected = qty === selectedValue;
              return (
                <li key={qty} ref={isSelected ? selectedRef : null}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`product-quantity-dropdown-option ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => {
                      onChange(qty);
                      setIsOpen(false);
                    }}
                  >
                    {qty}
                  </button>
                </li>
              );
            })}
            {hasMore ? (
              <li>
                <button
                  type="button"
                  className="product-quantity-dropdown-more"
                  onClick={() => setPage((current) => current + 1)}
                >
                  More
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
